import React, { useState } from 'react';

import { 
  Input,
  Label,
  Textarea,
  Skeleton,
  Button 
} from '@/components/ui';
import { User, Globe, MapPin, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

import type { ValidationError } from '@/utils/validation/types';
import { validateDomain, getErrorMessage } from '@/utils/validation/helpers';

import type { ClientFormData } from '../new_client/utilities/formDataUtils';
import { generateClientDescription } from '@/services/clientService';

import Logger from '@/utils/logger';

interface ClientBasicInfoFormProps {
  formData: ClientFormData;
  onUpdate: (field: keyof ClientFormData, value: ClientFormData[keyof ClientFormData]) => void;
  onFieldBlur?: (field: keyof ClientFormData) => void;
  isLoading?: boolean;
  className?: string;
  errors?: ValidationError[];
  touchedFields?: Set<string>;
  isAiGenerated?: boolean;
  setIsAiGenerated?: (value: boolean) => void;
}

export const ClientBasicInfoForm: React.FC<ClientBasicInfoFormProps> = ({
  formData,
  onUpdate,
  onFieldBlur,
  isLoading = false,
  className,
  errors = [],
  touchedFields = new Set(),
  isAiGenerated = false,
  setIsAiGenerated = () => {},
}) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateDescription = async () => {
    if (!formData.domain || !formData.domain.trim() || validateDomain(formData.domain)) {
      const validationError = validateDomain(formData.domain || '');
      Logger.warn('Domain validation failed for AI generation', { 
        component: 'ClientBasicInfoForm',
        domain: formData.domain,
        validationError,
        isEmpty: !formData.domain || !formData.domain.trim()
      });
      toast.error("Please enter a valid website domain first.");
      return;
    }

    setIsGenerating(true);

    try {
      let fullUrl = formData.domain;
      if (!fullUrl.startsWith('http')) {
        fullUrl = `https://${fullUrl}`;
      }

      const result = await generateClientDescription(fullUrl);

      if (result.description) {
        Logger.info('Updating form with generated description', { 
          component: 'ClientBasicInfoForm',
          fullUrl,
          previousDescription: formData.description?.substring(0, 50) || 'none',
          newDescriptionLength: result.description.length,
          newDescriptionPreview: result.description.substring(0, 100) + '...'
        });

        onUpdate('description', result.description);
        setIsAiGenerated(true);
      } else {
        Logger.warn('AI generation returned empty description', { 
          component: 'ClientBasicInfoForm',
          fullUrl,
          result
        });
        toast.error("Could not generate a description from the website.");
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      Logger.error("AI description generation failed in component", error instanceof Error ? error : new Error(message), {
        component: 'ClientBasicInfoForm',
        domain: formData.domain,
        fullUrl: formData.domain?.startsWith('http') ? formData.domain : `https://${formData.domain}`,
        errorMessage: message,
        errorStack: error instanceof Error ? error.stack : undefined
      });
      toast.error(`Error: ${message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  if (isLoading) {
    return (
      <div className={cn("space-y-6", className)}>
        <div className="space-y-2">
          <Skeleton className="h-5 w-24 mb-1.5" />
          <Skeleton className="h-10 w-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-5 w-24 mb-1.5" />
          <Skeleton className="h-20 w-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-5 w-32 mb-1.5" />
          <Skeleton className="h-10 w-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-5 w-32 mb-1.5" />
          <Skeleton className="h-20 w-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-5 w-48 mb-1.5" />
          <Skeleton className="h-20 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-6", className)}>
      <div className="space-y-2">
        <Label htmlFor="clientName" className="flex items-center gap-1.5 text-foreground">
          <User className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          Client Name
        </Label>
        <Input
          id="clientName"
          value={formData.name ?? ''}
          onChange={(e) => onUpdate('name', e.target.value)}
          onBlur={() => onFieldBlur?.('name')}
          disabled={isLoading}
          placeholder="Enter client's full name"
          required
          className={cn(
            "w-full transition-colors duration-200",
            getErrorMessage('name', errors, touchedFields) && "border-destructive focus-visible:ring-destructive"
          )}
          autoComplete="off"
          data-validation-key="name"
        />
        <AnimatePresence mode="wait">
          {getErrorMessage('name', errors, touchedFields) && (
            <motion.p
              key="name-error"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
              className="text-xs text-destructive mt-1 whitespace-nowrap text-right"
            >
              {getErrorMessage('name', errors, touchedFields)}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <Label htmlFor="clientDescription" className="text-foreground">Description</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleGenerateDescription}
            disabled={isGenerating || !formData.domain || !!validateDomain(formData.domain)}
            className="flex items-center gap-2"
          >
            <Sparkles className={cn("h-4 w-4", isGenerating && "animate-spin")} />
            {isGenerating ? 'Generating...' : 'Generate with AI'}
          </Button>
        </div>
        <div className="relative">
          <Textarea
            id="clientDescription"
            value={formData.description ?? ''}
            onChange={(e) => {
              const value = e.target.value || null;
              let finalValue = value;
              
              // For manual input, enforce character limit at input level
              if (!isAiGenerated && value && value.length > 500) {
                finalValue = value.substring(0, 500);
              }
              
              onUpdate('description', finalValue);
              if (isAiGenerated && finalValue !== formData.description) {
                setIsAiGenerated(false); // Mark as manually edited if user changes AI content
              }
            }}
            onBlur={() => onFieldBlur?.('description')}
            placeholder="Enter a description, or generate one from the client's website."
            disabled={isLoading || isGenerating}
            rows={3}
            className={cn("w-full", !isAiGenerated && "pr-16 pb-8")}
            maxLength={isAiGenerated ? undefined : 500}
          />
          {!isAiGenerated && (
            <div className="absolute bottom-1 right-2 pointer-events-none z-10">
              <span className="text-xs text-muted-foreground bg-background/90 px-2 py-1 rounded shadow-sm border border-border/20">
                {(formData.description || '').length}/500
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="clientDomain" className="flex items-center gap-1.5 text-foreground">
          <Globe className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          Website Domain
        </Label>
        <Input
          id="clientDomain"
          value={formData.domain ?? ''}
          onChange={(e) => onUpdate('domain', e.target.value || null)}
          onBlur={() => onFieldBlur?.('domain')}
          placeholder="e.g., example.com (for AI generation)"
          disabled={isLoading || isGenerating}
          type="text"
          inputMode="url"
          autoComplete="off"
          className={cn(
            "w-full transition-colors duration-200",
            getErrorMessage('domain', errors, touchedFields) && "border-destructive focus-visible:ring-destructive"
          )}
          data-validation-key="domain"
        />
        <AnimatePresence mode="wait">
          {getErrorMessage('domain', errors, touchedFields) ? (
            <motion.p
              key="domain-error"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
              className="text-sm text-destructive transition-colors duration-200"
            >
              {getErrorMessage('domain', errors, touchedFields)}
            </motion.p>
          ) : (
            <motion.p
              key="domain-helper"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
              className="text-sm text-muted-foreground transition-colors duration-200"
            >
              Used to generate a client description with AI.
            </motion.p>
          )}
        </AnimatePresence>
      </div>
      <div className="space-y-2">
        <Label htmlFor="clientPhysicalAddress" className="flex items-center gap-1.5 text-foreground">
          <MapPin className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          Physical Address
        </Label>
        <Textarea
          id="clientPhysicalAddress"
          value={formData.physical_address ?? ''}
          onChange={(e) => onUpdate('physical_address', e.target.value || null)}
          placeholder="Street, City, State, Zip (Optional)"
          disabled={isLoading}
          rows={3}
          className="w-full"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="clientBillingAddress" className="flex items-center gap-1.5 text-foreground">
          <MapPin className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          Billing Address (if different)
        </Label>
        <Textarea
          id="clientBillingAddress"
          value={formData.billing_address ?? ''}
          onChange={(e) => onUpdate('billing_address', e.target.value || null)}
          placeholder="Street, City, State, Zip (Optional)"
          disabled={isLoading}
          rows={3}
          className="w-full"
        />
        <p className="text-sm text-muted-foreground">Leave blank if the billing address is the same as the physical address.</p>
      </div>
    </div>
  );
};
